import {
    KiteButton,
    KiteCheckbox,
    KiteChip,
    KiteChipGroup,
    KiteIcon,
    KiteRadio,
    KiteRadioButton,
} from '@kite/react-kite';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useEffect, useMemo, useState } from 'react';
import { useKeyPress } from '../../shared/useKeyPress';
import { FloatingMenuArrow } from '../FloatingMenuArrow/FloatingMenuArrow';
import './LearnHeader.scss';

export const LearnHeader = ({
    articles,
    filters,
    setFilters,
    sort,
    setSort,
}) => {
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [sortExpanded, setSortExpanded] = useState(false);
    const [expandedFilterDropdownMap, setExpandedFilterDropdownMap] = useState(
        {}
    );
    const escPress = useKeyPress(`Escape`);

    const data = useStaticQuery(graphql`
        query {
            tags: allTaxonomyTermArticleCategories(
                sort: {name: ASC}
            ) {
                nodes {
                    name
                    relationships {
                        taxonomy_term__article_categories {
                            name
                        }
                    }
                }
            }
        }
    `);

    const {
        tags: { nodes: tags },
    } = data;

    const primaryTags = useMemo(() => {
        return tags.filter(
            (tag) => tag.relationships.taxonomy_term__article_categories?.length
        );
    }, [tags]);

    // Add or remove a filter depending on if it already exisits in the filters array
    const filterChanged = (name: string) => {
        if (filters.includes(name)) {
            filters.splice(filters.indexOf(name), 1);
            setFilters([...filters]);
        } else {
            filters.push(name);
            setFilters([...filters]);
        }
    };

    const reset = () => {
        setFilters([]);
    };

    // Collapse all primary filter catergory dropdown menus except the one passed in
    const collapseFilterDropdowns = (excludedFilter?: string) => {
        const map = {};
        for (const value in expandedFilterDropdownMap) {
            if (!excludedFilter || value !== excludedFilter) {
                map[value] = false;
            }
        }
        return map;
    };

    const handleFilterCategoryClick = (
        tagName: string,
        screenSize: `desktop` | `mobile` = `mobile`
    ) => {
        let map = expandedFilterDropdownMap;
        // If the screen size > the 'lg' breakpoint, only allow one
        // filter menu to be open at a time
        if (screenSize === `desktop`) {
            map = collapseFilterDropdowns(tagName);
        }
        setExpandedFilterDropdownMap((curMap) => {
            return {
                ...map,
                [tagName]: !curMap[tagName],
            };
        });
    };

    const isFilterCategoryExpanded = (tagName: string) => {
        return expandedFilterDropdownMap[tagName];
    };

    useEffect(() => {
        if (escPress) {
            setFiltersExpanded(false);
            setExpandedFilterDropdownMap(collapseFilterDropdowns());
            setSortExpanded(false);
        }
    }, [escPress]);

    // Create a state map of all primary filters with their default expanded value set to false.
    useEffect(() => {
        if (primaryTags) {
            const primaryFilterMap = primaryTags.reduce((acc, tag) => {
                acc[tag.name] = false;
                return acc;
            }, {});
            setExpandedFilterDropdownMap(primaryFilterMap);
        }
    }, []);

    // When the menu is in mobile view and opened, expand categories containing selected filters.
    const openFilterCategories = () => {
        if (typeof window !== undefined && window.innerWidth < 840) {
            const map = expandedFilterDropdownMap;
            primaryTags.forEach((tag) => {
                if (
                    tag?.relationships?.taxonomy_term__article_categories?.some(
                        (subTag) => filters.includes(subTag.name)
                    )
                ) {
                    map[tag.name] = true;
                } else {
                    map[tag.name] = false;
                }
            });
            setExpandedFilterDropdownMap(map);
        }
    };

    return (
        <header
            className={`bar ${filters.length ? `bar--with-filters` : ``}`}
            id="top"
        >
            <div className="kite-container">
                <h2 className="typestack-title-5">
                    Resources <small>[{articles.length}]</small>
                </h2>
                <div className="controls">
                    <button
                        role="button"
                        aria-expanded={filtersExpanded}
                        aria-controls="filters"
                        onClick={(e) => {
                            e.preventDefault();
                            setFiltersExpanded(!filtersExpanded);
                            setSortExpanded(false);
                            setExpandedFilterDropdownMap(
                                collapseFilterDropdowns()
                            );
                            openFilterCategories();
                        }}
                    >
                        Filter <KiteIcon icon="ki-filter" />
                    </button>
                    <FloatingMenuArrow hidden={!filtersExpanded} />
                    {/**
                     * Mobile view of the filters menu that stretches full screen.
                     * Displays a list of primary filter catergories that can be expanded
                     * to select individual filters.
                     */}
                    <menu
                        id="filters"
                        className="control-menu filters"
                        hidden={!filtersExpanded}
                    >
                        <li>
                            <h4>
                                <span>
                                    Filters
                                    {filters.length > 0 && (
                                        <p className="caption">{`[${filters.length} Selected]`}</p>
                                    )}
                                </span>
                                <button
                                    aria-label="close"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setFiltersExpanded(false);
                                    }}
                                >
                                    <KiteIcon icon="ki-x" />
                                </button>
                            </h4>
                            <KiteButton
                                className="clear-all-btn"
                                variant="borderless"
                                onClick={reset}
                            >
                                <>Clear All</>
                            </KiteButton>
                        </li>
                        {primaryTags.map((tag, idx) => {
                            const subTagsListId = `${tag.name
                                .toLowerCase()
                                .replace(` `, `-`)}_sub-tags`;
                            return (
                                <li key={tag.name}>
                                    <h5>
                                        <label
                                            htmlFor={`${tag.name}-category-btn`}
                                            className="filters__category-label"
                                        >
                                            {tag.name}
                                        </label>
                                        <KiteButton
                                            key={idx}
                                            name={tag.name}
                                            aria-label={
                                                isFilterCategoryExpanded(
                                                    tag.name
                                                )
                                                    ? `Collapse ${tag.name} filters`
                                                    : `Expand ${tag.name} filters`
                                            }
                                            aria-expanded={isFilterCategoryExpanded(
                                                tag.name
                                            )}
                                            aria-controls={`${subTagsListId}`}
                                            id={`${tag.name}-category-btn`}
                                            variant="borderless"
                                            onClick={() => {
                                                handleFilterCategoryClick(
                                                    tag.name
                                                );
                                            }}
                                        >
                                            <KiteIcon
                                                icon={
                                                    isFilterCategoryExpanded(
                                                        tag.name
                                                    )
                                                        ? `ki-minus`
                                                        : `ki-plus`
                                                }
                                            />
                                        </KiteButton>
                                    </h5>
                                    {expandedFilterDropdownMap[tag.name] && (
                                        <fieldset
                                            id={subTagsListId}
                                            className="sub-filters"
                                        >
                                            {tag.relationships.taxonomy_term__article_categories.map(
                                                (subTag) => (
                                                    <KiteCheckbox
                                                        key={subTag.name}
                                                        name={subTag.name}
                                                        id={`${subTag.name}`}
                                                        checked={filters.includes(
                                                            subTag.name
                                                        )}
                                                        onChange={() => {
                                                            filterChanged(
                                                                subTag.name
                                                            );
                                                        }}
                                                        label={subTag.name}
                                                    />
                                                )
                                            )}
                                        </fieldset>
                                    )}
                                </li>
                            );
                        })}
                    </menu>
                    <button
                        role="button"
                        aria-expanded={sortExpanded}
                        aria-controls="sort"
                        onClick={(e) => {
                            e.preventDefault();
                            setSortExpanded(!sortExpanded);
                            setFiltersExpanded(false);
                        }}
                    >
                        Sort By <KiteIcon icon="ki-sort" />
                    </button>
                    <FloatingMenuArrow hidden={!sortExpanded} />
                    <fieldset
                        id="sort"
                        className="control-menu"
                        hidden={!sortExpanded}
                    >
                        <h4>
                            Sort By
                            <button
                                aria-label="close"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSortExpanded(false);
                                }}
                            >
                                <KiteIcon icon="ki-x" />
                            </button>
                        </h4>
                        <KiteRadio onChange={(e) => setSort(e.target.value)}>
                            <KiteRadioButton
                                name="recent"
                                label="Most Recent"
                                value="recent"
                                checked={sort === `recent`}
                            />
                            <KiteRadioButton
                                name="alpha"
                                label="A to Z"
                                value="alpha"
                                checked={sort === `alpha`}
                            />
                        </KiteRadio>
                    </fieldset>
                </div>
            </div>
            {/**
             * Desktop view of the filters menu displayed on 'Filter' button click.
             * Presents a banner of primary filter catergories that can be expanded
             * to select individual filters.
             */}
            <fieldset
                id="filter-bar"
                className="filter-bar"
                hidden={!filtersExpanded}
            >
                <ul id="primary-tags-list" className="kite-container">
                    {primaryTags.map((tag, idx) => {
                        const subTagsListId = `${tag.name
                            .toLowerCase()
                            .replace(` `, `-`)}_sub-tags`;
                        return (
                            <li key={tag.name}>
                                <KiteButton
                                    className="dropdown-button"
                                    aria-controls={subTagsListId}
                                    aria-expanded={isFilterCategoryExpanded(
                                        tag.name
                                    )}
                                    variant="borderless"
                                    onClick={() =>
                                        handleFilterCategoryClick(
                                            tag.name,
                                            `desktop`
                                        )
                                    }
                                >
                                    {tag.name}
                                    <KiteIcon
                                        icon="ki-chevron-down"
                                        size=".75rem"
                                    />
                                </KiteButton>
                                <FloatingMenuArrow
                                    hidden={
                                        !expandedFilterDropdownMap[tag.name]
                                    }
                                />
                                <fieldset
                                    id={subTagsListId}
                                    className="sub-tags"
                                    hidden={
                                        !expandedFilterDropdownMap[tag.name]
                                    }
                                >
                                    {/**
                                     * The list of individual, selectable filter
                                     * checkboxes for each primary filter category
                                     */}
                                    {tag.relationships.taxonomy_term__article_categories.map(
                                        (subTag) => (
                                            <KiteCheckbox
                                                key={subTag.name}
                                                name={subTag.name}
                                                id={`${subTag.name}`}
                                                checked={filters.includes(
                                                    subTag.name
                                                )}
                                                onChange={() => {
                                                    filterChanged(subTag.name);
                                                }}
                                                label={subTag.name}
                                            />
                                        )
                                    )}
                                </fieldset>
                            </li>
                        );
                    })}
                </ul>
            </fieldset>
            {/**
             * Displays a horizontal banner with chips for each selected
             * filter on large screens. On chip click, removes the filter.
             * Also includes a "Clear All" filters button
             */}
            <div className="filter-chips" hidden={!filters.length}>
                <div className="kite-container">
                    <KiteChipGroup overflowBehavior="wrapped">
                        {filters.map((filter) => (
                            <KiteChip
                                key={filter}
                                label={filter}
                                value={filter}
                                onClick={() => filterChanged(filter)}
                                dismissible={true}
                            />
                        ))}
                    </KiteChipGroup>
                    <KiteButton
                        className="clear-all-btn"
                        variant="borderless"
                        onClick={reset}
                    >
                        <>
                            Clear All
                            <KiteIcon icon="ki-x" size="1.25rem" />
                        </>
                    </KiteButton>
                </div>
            </div>
        </header>
    );
};
